import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, finalize, map, of, shareReplay } from 'rxjs';
import { environment } from '../../environments/environment';
import { CmsSeo } from '../models/types';

export interface CmsApiResponse {
  status: boolean;
  message?: string;
  data?: {
    content?: string;
    identifier?: string;
    title?: string;
    page?: string;
    isActive?: boolean;
  };
}

function sanitizeCmsValue(val: any): any {
  if (typeof val === 'string') {
    return val
      .replace(/DEXOR\s*AI/gi, 'DEX')
      .replace(/STAKE\s+DXOR/gi, 'Stake Tokens')
      .replace(/STAKE\s+DEXOR/gi, 'Stake Tokens')
      .replace(/DXOR\s+staking/gi, 'token staking')
      .replace(/DEXOR\s+staking/gi, 'token staking')
      .replace(/\bDXOR\b/g, 'DEX')
      .replace(/\bDexor\b/g, 'DEX')
      .replace(/\bDEXOR\b/g, 'DEX');
  }
  if (Array.isArray(val)) {
    return val.map(sanitizeCmsValue);
  }
  if (val !== null && typeof val === 'object') {
    const res: any = {};
    for (const key of Object.keys(val)) {
      res[key] = sanitizeCmsValue(val[key]);
    }
    return res;
  }
  return val;
}

@Injectable({
  providedIn: 'root',
})
export class CmsPageService {
  private http = inject(HttpClient);
  private inflight = new Map<string, Observable<CmsApiResponse | null>>();

  fetchByIdentifier(identifier: string): Observable<CmsApiResponse | null> {
    const existing = this.inflight.get(identifier);
    if (existing) {
      return existing;
    }

    const request = this.http
      .get<CmsApiResponse>(`${environment.apiUrl}/user/cms`, {
        params: { identifier },
      })
      .pipe(
        map((res) => {
          if (res && res.data) {
            if (typeof res.data.content === 'string') {
              try {
                const parsed = JSON.parse(res.data.content);
                res.data.content = JSON.stringify(sanitizeCmsValue(parsed));
              } catch {
                res.data.content = sanitizeCmsValue(res.data.content);
              }
            }
            if (res.data.title) {
              res.data.title = sanitizeCmsValue(res.data.title);
            }
          }
          return res;
        }),
        catchError(() => of(null)),
        shareReplay(1),
        finalize(() => this.inflight.delete(identifier)),
      );

    this.inflight.set(identifier, request);
    return request;
  }

  applyPageSeo(seo: CmsSeo) {
    if (typeof document === 'undefined') return;

    document.title = seo.metaTitle;

    const setMeta = (name: string, content: string, attr: 'name' | 'property' = 'name') => {
      if (!content) return;
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    setMeta('description', seo.metaDescription);
    setMeta('keywords', seo.keywords);
    setMeta('og:title', seo.metaTitle, 'property');
    setMeta('og:description', seo.metaDescription, 'property');
    if (seo.ogImage) {
      setMeta('og:image', seo.ogImage, 'property');
    }
  }
}
